import { Box, Button, Checkbox, Container, Flex, Heading } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { Field } from '@/components/core/inputs/Field';
import { FadeInModalContext } from '@/components/layouts/fadeInModalContext';
import { useLogInForm } from '@/components/ui/features/log-in/hooks/useLogInForm';
import {
  AAPTSocialSignInButton,
  GoogleSocialSignInButton,
} from '@/components/ui/shared/social/SocialSignInButton/SocialSignInButton';
import { useAnalytics } from '@/context/Analytics';
import { AccessFrom } from '@/enums/AccessFrom';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';

import { OverlayType } from '../../../../../enums';

export const LogInFormSection: FC = () => {
  const { register, watch, errors, serverErrors, onSubmit } = useLogInForm();
  const context = useContext(FadeInModalContext);
  const { t } = useTranslation();

  const router = useRouter();

  const analytics = useAnalytics();

  const { accessFrom = AccessFrom.Core } = useQueryParams<{ accessFrom: AccessFrom }>({
    accessFrom: StringParam,
  });

  function handleCreateAccountClick() {
    analytics.track('user_account_create_click', { location: 'log_in' });
    router.push(`/create-account?accessFrom=${accessFrom}`);
  }

  return (
    <Container>
      <Box px={{ base: 5, md: 0 }} pb={{ base: 6, md: 0 }} pt={8} maxW={{ base: 'full', md: '360px' }} mx="auto">
        <Flex as="form" flexDirection="column" justifyContent="center" onSubmit={onSubmit}>
          <Heading fontSize={{ base: '2rem', md: '3rem' }} mb={12}>
            Log in
          </Heading>
          <Box mb={5}>
            <Field
              {...register('email', {
                required: 'Email address is required',
              })}
              label="Email"
              hasValue={Boolean(watch('email'))}
              isError={Boolean(errors['email']) || serverErrors.length > 0}
              autoComplete="email"
            />
            <ErrorMessage errors={errors} name="email" />
          </Box>
          <Box mb={5}>
            <Field
              {...register('password', {
                required: 'Password is required',
              })}
              label="Your password"
              type="password"
              hasValue={Boolean(watch('password'))}
              isError={Boolean(errors['password']) || serverErrors.length > 0}
              autoComplete="current-password"
            />
            <ErrorMessage errors={errors} name="password" />
          </Box>

          <Checkbox size="lg" colorScheme="violet" defaultChecked {...register('remember_me')}>
            Remember me
          </Checkbox>

          <Button type="submit" mt={{ base: 10, md: 14 }} mb={{ base: 4, md: 6 }}>
            log in
          </Button>

          <Button variant="unstyled" onClick={() => context.showModalType.set(OverlayType.RESET_PASSWORD)}>
            Forgot password?
          </Button>

          <Flex flexDirection="column" mt={{ base: 12, md: '3.75rem' }} mb={{ base: 8, md: 10 }}>
            <GoogleSocialSignInButton actionType="login">{t('googleLogin')}</GoogleSocialSignInButton>

            <AAPTSocialSignInButton css={{ marginTop: '10px' }}>{t('aaptLogin')}</AAPTSocialSignInButton>
          </Flex>

          <Flex whiteSpace="pre-wrap" mb={2} alignItems="center">
            <span>Don’t have an account? </span>
            <Button variant="unstyled" onClick={handleCreateAccountClick}>
              Create account
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Container>
  );
};
